"use client"

import { useEvent } from "@/app/hooks/use-event"
import { ArrowDownIcon } from "@/app/icons/arrow-down"
import { PriceWrapper } from "@/app/price"

import { Trans } from "@lingui/react/macro"
import {
  Button,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useMemo, useState } from "react"
import { useCollectionItemsOwnedByUserCountReatom as useCollectionItemsOwnedByUserCount } from "../../items/user-items/store/hooks.reatom"
import { useHighestCollectionOffer } from "../../offers/hooks.reatom"
import { useCollection } from "../../store/use-collection"
import { SellDialog } from "../sell-dialog/sell-dialog"
import { useCollectionNftsSellState } from "../use-collection-nfts-sell.reatom"

export const SellButtonView = reatomComponent(() => {
  const [openDialog, setOpenDialog] = useState(false)
  const collection = useCollection()

  const { sellPriceAtom, bestSellOfferAtom } = useCollectionNftsSellState()
  const sellPrice = sellPriceAtom()
  const bestSellOffer = bestSellOfferAtom()
  const userNftsCount = useCollectionItemsOwnedByUserCount()
  const offer = useHighestCollectionOffer()

  const message = useMemo(() => {
    if (!userNftsCount) {
      return <Trans>You don't have any NFTs to sell</Trans>
    }

    if (!offer) {
      return <Trans>There are no offers for this collection</Trans>
    }

    return null
  }, [userNftsCount, offer])

  const disabled = !userNftsCount || !offer

  const onClick = useEvent(() => {
    if (sellPrice) {
      if (!bestSellOffer) {
        return
      }
      setOpenDialog(true)
    } else {
      setOpenDialog(true)
    }
  })

  if (!offer) return null

  if (!disabled) {
    return (
      <>
        <SellDialog
          onOpenChange={setOpenDialog}
          open={openDialog}
          isMultiple={!sellPrice}
        />

        <SellButtonContent onClick={onClick} id="sell-dialog-trigger" />
      </>
    )
  }

  return (
    <Tooltip>
      <TooltipTrigger className="cursor-not-allowed" asChild>
        <div>
          <SellButtonContent disabled />
        </div>
      </TooltipTrigger>
      <TooltipContent>{message}</TooltipContent>
    </Tooltip>
  )
}, "SellButtonView")

const SellButtonContent = reatomComponent<React.ComponentProps<typeof Button>>(
  (props) => {
    const offer = useHighestCollectionOffer()

    return (
      <Button
        className="bg-destructive/20 text-white text-xs hover:bg-destructive/40"
        {...props}
      >
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="rounded-full bg-destructive p-1 text-black">
              <ArrowDownIcon className="rotate-180 [&&]:h-[10px] [&&]:w-[10px]" />
            </div>
            <span className="uppercase">
              <Trans>Sell</Trans>
            </span>
          </div>
          <span className="sr-only md:not-sr-only">
            {offer ? (
              <PriceWrapper currencyClassName="text-white" {...offer} />
            ) : (
              <span>-</span>
            )}
          </span>
        </div>
      </Button>
    )
  },
  "SellButtonContent",
)

export function ButtonSkeleton() {
  return <Skeleton className="h-9 w-[210px]" />
}
